/**
 * VoiceProvider Context
 *
 * A React context provider for managing global voice settings and state.
 * Provides centralized configuration for speech recognition and synthesis
 * across the entire application.
 *
 * Features:
 * - Global voice settings (language, rate, pitch, voice)
 * - Browser capability detection
 * - Persistent settings via localStorage
 * - Easy access via useVoice hook
 *
 * @example
 * // Wrap your app
 * <VoiceProvider>
 *   <App />
 * </VoiceProvider>
 *
 * // Use in components
 * const { settings, setLanguage, isSupported } = useVoice();
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Check for browser support
const speechRecognitionSupported =
  typeof window !== 'undefined' &&
  ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

const speechSynthesisSupported =
  typeof window !== 'undefined' && 'speechSynthesis' in window;

// Default settings - optimized for soft, natural female voice
const DEFAULT_SETTINGS = {
  language: 'en-US',
  rate: 0.92, // Slightly slower for more natural, conversational feel
  pitch: 1.08, // Slightly higher for softer female voice tone
  volume: 0.9, // Slightly lower volume for gentler delivery
  voiceName: null, // Will be set to preferred female voice
  autoReadMessages: false, // Auto-read new chat messages
  autoReadAlerts: true, // Auto-read travel advisories
  showVoiceButtons: true, // Show voice input/output buttons
};

// Preferred female voice patterns (prioritized in order)
const PREFERRED_FEMALE_VOICES = [
  // High-quality natural female voices
  'Samantha', // macOS/iOS - very natural
  'Karen', // macOS/iOS Australian
  'Moira', // macOS/iOS Irish
  'Tessa', // macOS/iOS South African
  'Fiona', // macOS/iOS Scottish
  'Victoria', // macOS
  'Google US English', // Chrome - female
  'Google UK English Female',
  'Microsoft Zira', // Windows US
  'Microsoft Hazel', // Windows UK
  'Microsoft Susan', // Windows UK
  'Aria', // Edge natural voice
  'Jenny', // Edge natural voice
  'Sara', // Edge natural voice
  // Pattern matches for female voices
  'Female',
  'female',
  'Woman',
  'woman',
];

// Storage key for persisting settings
const STORAGE_KEY = 'itineraryseek_voice_settings';

// Create context
const VoiceContext = createContext(null);

/**
 * VoiceProvider component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @param {Object} props.defaultSettings - Override default settings
 */
export const VoiceProvider = ({ children, defaultSettings = {} }) => {
  const [settings, setSettings] = useState(() => {
    // Try to load settings from localStorage
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          return { ...DEFAULT_SETTINGS, ...JSON.parse(stored), ...defaultSettings };
        }
      } catch (e) {
        console.warn('Failed to load voice settings:', e);
      }
    }
    return { ...DEFAULT_SETTINGS, ...defaultSettings };
  });

  const [voices, setVoices] = useState([]);
  const [currentVoice, setCurrentVoice] = useState(null);

  // Load available voices
  useEffect(() => {
    if (!speechSynthesisSupported) {
      return;
    }

    const loadVoices = () => {
      const availableVoices = window.speechSynthesis.getVoices();
      if (availableVoices.length > 0) {
        setVoices(availableVoices);

        // Set current voice from settings or find default
        if (settings.voiceName) {
          const savedVoice = availableVoices.find((v) => v.name === settings.voiceName);
          if (savedVoice) {
            setCurrentVoice(savedVoice);
            return;
          }
        }

        // Filter voices by language
        const langPrefix = settings.language.split('-')[0];
        const languageVoices = availableVoices.filter((v) =>
          v.lang.startsWith(langPrefix)
        );
        const englishVoices = availableVoices.filter((v) => v.lang.startsWith('en'));
        const candidateVoices = languageVoices.length > 0 ? languageVoices : englishVoices;

        // Find preferred female voice from the candidate voices
        let selectedVoice = null;

        // First, try to find a voice matching preferred female voice patterns
        for (const pattern of PREFERRED_FEMALE_VOICES) {
          const match = candidateVoices.find((v) =>
            v.name.includes(pattern)
          );
          if (match) {
            selectedVoice = match;
            break;
          }
        }

        // If no preferred female voice found, try to find any voice with "female" characteristics
        if (!selectedVoice) {
          // Look for voices that are likely female based on common naming patterns
          selectedVoice = candidateVoices.find((v) => {
            const name = v.name.toLowerCase();
            return name.includes('female') ||
                   name.includes('woman') ||
                   // Common female names used in TTS
                   /\b(samantha|karen|victoria|zira|hazel|susan|aria|jenny|sara|allison|ava|joanna|kendra|kimberly|salli|ivy|nicole|emma|amy|raveena|aditi|lupe|penelope|camila|lucia|mia|conchita|zhiyu|mizuki|nanami|seoyeon|sunhi)\b/i.test(name);
          });
        }

        // Fall back to first available voice if no female voice found
        setCurrentVoice(selectedVoice || candidateVoices[0] || availableVoices[0]);
      }
    };

    loadVoices();

    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      if (window.speechSynthesis.onvoiceschanged !== undefined) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, [settings.language, settings.voiceName]);

  // Persist settings to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
      } catch (e) {
        console.warn('Failed to save voice settings:', e);
      }
    }
  }, [settings]);

  // Update individual settings
  const updateSettings = useCallback((updates) => {
    setSettings((prev) => ({ ...prev, ...updates }));
  }, []);

  const setLanguage = useCallback((language) => {
    updateSettings({ language });
  }, [updateSettings]);

  const setRate = useCallback((rate) => {
    updateSettings({ rate: Math.max(0.5, Math.min(2, rate)) });
  }, [updateSettings]);

  const setPitch = useCallback((pitch) => {
    updateSettings({ pitch: Math.max(0, Math.min(2, pitch)) });
  }, [updateSettings]);

  const setVolume = useCallback((volume) => {
    updateSettings({ volume: Math.max(0, Math.min(1, volume)) });
  }, [updateSettings]);

  const setVoiceName = useCallback(
    (voiceName) => {
      updateSettings({ voiceName });
      const voice = voices.find((v) => v.name === voiceName);
      if (voice) {
        setCurrentVoice(voice);
      }
    },
    [voices, updateSettings]
  );

  const setAutoReadMessages = useCallback((enabled) => {
    updateSettings({ autoReadMessages: enabled });
  }, [updateSettings]);

  const setAutoReadAlerts = useCallback((enabled) => {
    updateSettings({ autoReadAlerts: enabled });
  }, [updateSettings]);

  const setShowVoiceButtons = useCallback((enabled) => {
    updateSettings({ showVoiceButtons: enabled });
  }, [updateSettings]);

  // Reset to defaults
  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // Get voices filtered by language
  const getVoicesByLanguage = useCallback(
    (lang) => {
      const langPrefix = (lang || settings.language).split('-')[0];
      return voices.filter((v) => v.lang.startsWith(langPrefix));
    },
    [voices, settings.language]
  );

  // Context value
  const value = {
    // Browser support
    isSupported: {
      speechRecognition: speechRecognitionSupported,
      speechSynthesis: speechSynthesisSupported,
      any: speechRecognitionSupported || speechSynthesisSupported,
      all: speechRecognitionSupported && speechSynthesisSupported,
    },

    // Settings
    settings,
    updateSettings,

    // Individual setters
    setLanguage,
    setRate,
    setPitch,
    setVolume,
    setVoiceName,
    setAutoReadMessages,
    setAutoReadAlerts,
    setShowVoiceButtons,
    resetSettings,

    // Voices
    voices,
    currentVoice,
    getVoicesByLanguage,
  };

  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
};

/**
 * useVoice hook - Access voice context
 * @returns {Object} Voice context value
 * @throws {Error} If used outside VoiceProvider
 */
export const useVoice = () => {
  const context = useContext(VoiceContext);

  if (!context) {
    throw new Error('useVoice must be used within a VoiceProvider');
  }

  return context;
};

/**
 * useVoiceSupport hook - Quick check for voice support
 * Can be used without VoiceProvider
 * @returns {Object} Support status for speech recognition and synthesis
 */
export const useVoiceSupport = () => {
  return {
    speechRecognition: speechRecognitionSupported,
    speechSynthesis: speechSynthesisSupported,
    any: speechRecognitionSupported || speechSynthesisSupported,
    all: speechRecognitionSupported && speechSynthesisSupported,
  };
};

export default VoiceProvider;
